import { useParams } from 'react-router-dom'
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs'
import { useDebounceValue } from 'usehooks-ts'
import PageContainer from '@/common/components/PageContainer'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { DataTableSimpleSearch } from '@/common/ui/table/data-table-simple-search'
import { DataTableResetFilter } from '@/common/ui/table/data-table-reset-filter'
import { Button } from '@/common/ui/button'
import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { ColumnDef } from '@tanstack/react-table'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import useQueryGetTurnoverEventById from '../../api/hooks/useQueryGetTurnoverEventById'
import { getTurnoverEventClaims } from '../../api/services'
import { useQuery } from '@tanstack/react-query'
import { EventsQueryKeys } from '../../keys'
import {
	getEventStatusLabel,
	getPeriodTypeLabel,
	EVENT_STATUS_BADGE_VARIANT,
} from '../../constants'
import { formatCurrency } from '@/common/lib/utils'
import type { TurnoverEventClaimData } from '../../type'

const claimColumns: ColumnDef<TurnoverEventClaimData>[] = [
	{
		accessorKey: 'playerUsername',
		header: 'ผู้เล่น',
		cell: ({ row }) => (
			<div className="space-y-1">
				<div className="font-medium">{row.original.playerUsername}</div>
				<div className="text-sm text-muted-foreground">
					{row.original.playerPhone}
				</div>
			</div>
		),
	},
	{
		accessorKey: 'turnoverAchieved',
		header: 'ยอดเทิร์นที่ทำได้',
		cell: ({ row }) => formatCurrency(Number(row.original.turnoverAchieved)),
	},
	{
		accessorKey: 'amount',
		header: 'จำนวนรางวัล',
		cell: ({ row }) => (
			<div className="font-semibold text-green-600">
				{formatCurrency(Number(row.original.amount))}
			</div>
		),
	},
	{
		accessorKey: 'periodStartDate',
		header: 'ช่วงเวลา',
		cell: ({ row }) =>
			format(new Date(row.original.periodStartDate), 'dd MMM yyyy', {
				locale: th,
			}),
	},
	{
		accessorKey: 'createdAt',
		header: 'วันที่รับรางวัล',
		cell: ({ row }) =>
			format(new Date(row.original.createdAt), 'dd MMM yyyy HH:mm', {
				locale: th,
			}),
	},
]

function EventClaimsPage() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()

	const [queryParams] = useQueryStates({
		page: parseAsInteger.withOptions({ shallow: false }).withDefault(1),
		limit: parseAsInteger.withOptions({ shallow: false }).withDefault(10),
		q: parseAsString.withOptions({ shallow: false }).withDefault(''),
	})

	const [debouncedQuery] = useDebounceValue(queryParams.q, 500)

	const { data: event, isLoading: isLoadingEvent } =
		useQueryGetTurnoverEventById(id!)

	const { data: claims, isLoading: isLoadingClaims } = useQuery({
		queryKey: [
			EventsQueryKeys.getTurnoverEventClaims,
			id,
			{
				take: queryParams.limit,
				skip: (queryParams.page - 1) * queryParams.limit,
				playerName: debouncedQuery || undefined,
			},
		],
		queryFn: () =>
			getTurnoverEventClaims(id!, {
				take: queryParams.limit,
				skip: (queryParams.page - 1) * queryParams.limit,
				playerName: debouncedQuery || undefined,
			}),
		enabled: !!id,
	})

	if (isLoadingEvent) {
		return (
			<PageContainer>
				<Card>
					<CardContent className="p-6">
						<div className="flex h-32 items-center justify-center">
							<div className="text-muted-foreground">กำลังโหลด...</div>
						</div>
					</CardContent>
				</Card>
			</PageContainer>
		)
	}

	if (!event) {
		return (
			<PageContainer>
				<Card>
					<CardContent className="p-6">
						<div className="flex h-32 items-center justify-center">
							<div className="text-muted-foreground">ไม่พบข้อมูลกิจกรรม</div>
						</div>
					</CardContent>
				</Card>
			</PageContainer>
		)
	}

	return (
		<PageContainer>
			<div className="space-y-6">
				<div className="flex items-center gap-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate('/operator/events')}
					>
						<ArrowLeft className="mr-2 h-4 w-4" />
						กลับ
					</Button>
				</div>

				<Card>
					<CardHeader>
						<div className="flex items-start justify-between">
							<div className="space-y-2">
								<CardTitle className="text-2xl">{event.title}</CardTitle>
								{event.description && (
									<p className="text-sm text-muted-foreground">
										{event.description}
									</p>
								)}
							</div>
							<Badge variant={EVENT_STATUS_BADGE_VARIANT[event.status]}>
								{getEventStatusLabel(event.status)}
							</Badge>
						</div>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-2 gap-6 md:grid-cols-4">
							<div className="space-y-1">
								<div className="text-sm text-muted-foreground">
									ยอดเทิร์นที่ต้องการ
								</div>
								<div className="text-lg font-semibold">
									{formatCurrency(Number(event.requiredTurnover))}
								</div>
							</div>
							<div className="space-y-1">
								<div className="text-sm text-muted-foreground">รางวัล</div>
								<div className="text-lg font-semibold text-green-600">
									{formatCurrency(Number(event.rewardAmount))}
								</div>
							</div>
							<div className="space-y-1">
								<div className="text-sm text-muted-foreground">รอบระยะเวลา</div>
								<div className="text-lg font-semibold">
									{getPeriodTypeLabel(event.periodType)}
								</div>
							</div>
							<div className="space-y-1">
								<div className="text-sm text-muted-foreground">
									จำนวนผู้เคลม
								</div>
								<div className="text-lg font-semibold">{event.totalClaims}</div>
							</div>
							<div className="space-y-1">
								<div className="text-sm text-muted-foreground">
									ยอดรางวัลรวม
								</div>
								<div className="text-lg font-semibold">
									{formatCurrency(Number(event.totalRewardAmount))}
								</div>
							</div>
							<div className="space-y-1">
								<div className="text-sm text-muted-foreground">วันที่เริ่ม</div>
								<div className="font-medium">
									{format(new Date(event.startDate), 'dd MMM yyyy HH:mm', {
										locale: th,
									})}
								</div>
							</div>
							<div className="space-y-1">
								<div className="text-sm text-muted-foreground">
									วันที่สิ้นสุด
								</div>
								<div className="font-medium">
									{format(new Date(event.endDate), 'dd MMM yyyy HH:mm', {
										locale: th,
									})}
								</div>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>ประวัติการรับรางวัล</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							<div className="flex flex-wrap items-center gap-4">
								<DataTableSimpleSearch placeHolder="ค้นหาชื่อผู้เล่นหรือเบอร์โทร..." />
								<DataTableResetFilter />
							</div>
							<DataTablePaginate
								columns={claimColumns}
								data={claims?.data || []}
								totalItems={claims?.total || 0}
								isLoading={isLoadingClaims}
							/>
						</div>
					</CardContent>
				</Card>
			</div>
		</PageContainer>
	)
}

const GuardedEventClaimsPage = withUserPermission(EventClaimsPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.WEB_SETTING],
})

export default GuardedEventClaimsPage
